import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
} from "react";
import { useLogs } from "@plutoxyz/react-frame";
import Button from "../Button";
import {
  Send,
  Lightbulb,
  DollarSign,
  Globe,
  Zap,
  Check,
  ArrowUp,
} from "lucide-react";
import { fetchPythonScript } from "./llmUtils";
import AgentMessage, { parseAgentMessage } from "./AgentMessage";

interface Props {
  onGenerateCode: (code: string) => void;
  onInputChange: () => void;
  error?: string | null;
  onClearError?: () => void;
}

export interface LLMHandle {
  run: () => void;
}

interface ChatMessage {
  id: string;
  type: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface LogChunk {
  id: string;
  logs: string[];
  isComplete: boolean;
}

interface PresetPrompt {
  icon: React.ReactNode;
  label: string;
  prompt: string;
}

const presetPrompts: PresetPrompt[] = [
  {
    icon: <DollarSign size={16} />,
    label: "Bank Balance",
    prompt: `- go to https://pseudo-bank.pluto.dev
- prompt user with appropriate fields
- login with the username and password provided by the user
- get the balance of the account with id 2. The balance is in the format $X,XXX.XX. Parse the balance as a float.
- prove the final result only once.`,
  },
  //   {
  //     icon: <Globe size={16} />,
  //     label: "Web Scraping",
  //     prompt: `- go to https://example.com
  // - extract the main heading text
  // - scroll down to find contact information
  // - prove the extracted data`,
  //   },
  //   {
  //     icon: <Zap size={16} />,
  //     label: "Form Automation",
  //     prompt: `- navigate to a contact form
  // - fill out all required fields with test data
  // - submit the form
  // - verify successful submission`,
  //   },
];

const LLM = forwardRef<LLMHandle, Props>(
  ({ onGenerateCode, onInputChange, error, onClearError }, ref) => {
    const logs = useLogs();
    const [prompt, setPrompt] = useState("");
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isThinking, setIsThinking] = useState(false);
    const [logChunks, setLogChunks] = useState<LogChunk[]>([]);
    const [awaitingInput, setAwaitingInput] = useState(false);
    const [sessionComplete, setSessionComplete] = useState(false);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const lastProcessedLogIndex = useRef<number>(0);

    const [minHeight, setMinHeight] = useState(400);

    useEffect(() => {
      const updateHeight = () => {
        const calculatedHeight = document.documentElement.clientHeight - 160;
        setMinHeight(Math.max(calculatedHeight, 400));
      };

      window.addEventListener("resize", updateHeight);
      updateHeight(); // Set initial height

      return () => window.removeEventListener("resize", updateHeight);
    }, []);

    useImperativeHandle(ref, () => ({
      run() {
        handleSendMessage();
      },
    }));

    // Filter logs to only show [agent] logs
    const filterAgentLogs = (logs: string[]): string[] => {
      return logs.filter((log) => log.includes("[agent]"));
    };

    // Check if session is complete
    useEffect(() => {
      const hasSessionComplete = logs.some((log) =>
        log.includes("🎉 session complete"),
      );
      if (hasSessionComplete && !sessionComplete) {
        setSessionComplete(true);
        setIsThinking(false);
      }
    }, [logs, sessionComplete]);

    // Handle errors from PlutoFrame
    useEffect(() => {
      if (error) {
        setIsThinking(false);
        setAwaitingInput(false);
        if (onClearError) {
          // Auto-clear error after showing it
          const timer = setTimeout(() => {
            onClearError();
          }, 5000);
          return () => clearTimeout(timer);
        }
      }
    }, [error, onClearError]);

    // Handle streaming agent logs and chunk them based on prompt_user
    useEffect(() => {
      const agentLogs = filterAgentLogs(logs);
      const newLogs = agentLogs.slice(lastProcessedLogIndex.current);

      if (newLogs.length === 0) return;

      // Stop thinking as soon as we get the first agent log
      if (isThinking && agentLogs.length > 0) {
        setIsThinking(false);
      }

      // Process new logs to detect prompt_user and create chunks
      setLogChunks((prevChunks) => {
        const updatedChunks = [...prevChunks];
        let currentChunkIndex = updatedChunks.length - 1;

        // If no chunks exist or the last chunk is complete, start a new one
        if (
          currentChunkIndex < 0 ||
          updatedChunks[currentChunkIndex].isComplete
        ) {
          updatedChunks.push({
            id: `chunk-${Date.now()}`,
            logs: [],
            isComplete: false,
          });
          currentChunkIndex = updatedChunks.length - 1;
        }

        for (const log of newLogs) {
          // Add log to current chunk
          updatedChunks[currentChunkIndex].logs.push(log);

          // Check if this log contains prompt_user - if so, complete this chunk and set awaiting state
          if (log.includes('"prompt_user":')) {
            updatedChunks[currentChunkIndex].isComplete = true;
            setAwaitingInput(true);
          }
        }

        return updatedChunks;
      });

      // Remove awaiting input state when new logs come in after prompt_user
      if (awaitingInput && newLogs.length > 0) {
        const hasPromptUser = newLogs.some((log) =>
          log.includes('"prompt_user":'),
        );
        if (!hasPromptUser) {
          setAwaitingInput(false);
        }
      }

      lastProcessedLogIndex.current = agentLogs.length;
    }, [logs, isThinking, awaitingInput]);

    // Auto scroll to bottom when new messages arrive
    useEffect(() => {
      if (chatContainerRef.current) {
        const container = chatContainerRef.current;
        container.scrollTop = container.scrollHeight;
      }
    }, [messages, logChunks, isThinking, awaitingInput, sessionComplete]);

    const handleSendMessage = async () => {
      if (!prompt.trim()) {
        alert("Please enter a prompt");
        return;
      }

      // Add user message
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        type: "user",
        content: prompt,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsThinking(true);
      setSessionComplete(false);
      setAwaitingInput(false);
      setLogChunks([]);
      lastProcessedLogIndex.current = 0;

      try {
        const code = await fetchPythonScript({
          prompt,
        });
        onGenerateCode(code);
        setPrompt("");
        onInputChange();

        // Auto-resize textarea
        if (textareaRef.current) {
          textareaRef.current.style.height = "auto";
        }
      } catch (err: any) {
        console.error(err);
        setIsThinking(false);
      }
    };

    const handlePresetClick = (presetPrompt: string) => {
      setPrompt(presetPrompt);
      onInputChange();
      // Auto-resize textarea
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.height =
          textareaRef.current.scrollHeight + "px";
      }
    };

    const handleTextareaChange = (
      e: React.ChangeEvent<HTMLTextAreaElement>,
    ) => {
      setPrompt(e.target.value);
      onInputChange();

      // Auto-resize textarea
      e.target.style.height = "auto";
      e.target.style.height = e.target.scrollHeight + "px";
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    };

    return (
      <div
        className="flex flex-col h-full flex-1 overflow-hidden"
        style={{ minHeight: `${minHeight}px` }}
      >
        {/* Chat Area */}
        <div
          ref={chatContainerRef}
          className="flex-1 overflow-y-auto p-4 overflow-x-hidden space-y-2 min-h-[300px] max-h-[calc(100vh-280px)]"
        >
          {messages.map((message, idx) => (
            <div
              key={message.id}
              className={`flex flex-col ${message.type === "user" ? "justify-end" : "justify-start"}`}
            >
              {/* {message.type === "user" && idx === 0 && (
                <div className="flex items-center space-x-2 mb-1">
                  <p className="text-sm text-gray-900">TASK</p>
                </div>
              )} */}
              <div
                className={`max-w-[85%] rounded-md p-3 ${
                  message.type === "user"
                    ? "bg-gray-100 text-gray-900 ml-auto"
                    : "bg-gray-100 text-gray-900"
                }`}
              >
                <pre className="whitespace-pre-wrap font-sans text-sm">
                  {message.content}
                </pre>
              </div>
            </div>
          ))}

          {/* Thinking indicator */}
          {isThinking && (
            <div className="flex justify-start">
              <div className="bg-gray-100 text-gray-900 rounded-md p-3">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0.1s" }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0.2s" }}
                    ></div>
                  </div>
                  <span className="text-sm text-gray-600">Thinking...</span>
                </div>
              </div>
            </div>
          )}

          {/* Individual agent logs as separate messages */}
          {logChunks.flatMap((chunk) =>
            chunk.logs.map((log, logIndex) => {
              const parsedMessage = parseAgentMessage(log);
              return (
                <AgentMessage
                  key={`${chunk.id}-${logIndex}`}
                  message={parsedMessage}
                  useColoredHeaders={false}
                />
              );
            }),
          )}

          {/* Awaiting user input indicator */}
          {awaitingInput && (
            <div className="flex justify-start">
              <div className="bg-yellow-100 text-yellow-800 rounded-md p-3">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full animate-bounce"></div>
                    <div
                      className="w-2 h-2 bg-yellow-500 rounded-full animate-bounce"
                      style={{ animationDelay: "0.1s" }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-yellow-500 rounded-full animate-bounce"
                      style={{ animationDelay: "0.2s" }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium">
                    Awaiting user input...
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className="flex justify-start">
              <div className="w-full bg-red-50 border border-red-200 rounded-md p-3">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">!</span>
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-medium text-red-800">
                      Error occurred
                    </span>
                    <div className="text-sm text-red-700 mt-1 font-mono">
                      {error}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="border-t border-gray-200 p-4">
          {/* Message Input */}
          <div className="flex flex-col border border-gray-200 rounded-md p-2">
            <div className="mb-2 relative">
              <textarea
                ref={textareaRef}
                className="w-full rounded-md py-1 px-2 resize-none min-h-[30px] max-h-[100px] text-sm focus:outline-none"
                value={prompt}
                onChange={handleTextareaChange}
                onKeyPress={handleKeyPress}
                placeholder="Enter your prompt here... (Shift+Enter for new line)"
                rows={2}
              />
            </div>

            {/* Horizontal divider */}
            <hr className="border-gray-200 mb-2" />

            {/* Bottom row: Preset buttons on left, Send button on right */}
            <div className="flex items-center justify-between">
              <div className="flex space-x-2">
                {presetPrompts.map((preset, index) => (
                  <button
                    key={index}
                    onClick={() => handlePresetClick(preset.prompt)}
                    className="hover:cursor-pointer flex items-center space-x-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-xs md:text-sm transition-colors"
                  >
                    <span className="hidden md:block">{preset.icon}</span>
                    <span>{preset.label}</span>
                  </button>
                ))}
              </div>

              <button
                onClick={handleSendMessage}
                disabled={!prompt.trim() || isThinking}
                className="text-sm hover:cursor-pointer flex items-center md:space-x-2 px-2 md:px-4 py-2 bg-[#414552] hover:bg-[#414552cc] disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-md transition-colors "
              >
                <ArrowUp size={16} />
                <span className="hidden md:block">Send</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

export default LLM;
